import type { TFunction } from "i18next";
import type {
  Agent,
  AgentBrain,
  AiComputer,
  AiStatus,
  BrainUnavailable,
  ConnectIntent,
  Edition,
  Machine,
  MachineAccount,
  MachineAccountKind,
} from "../api/types";
import { ownSignInReady } from "./ai-accounts";
import { answersOthers, audienceOf } from "./audience";
import { type SetupView, setupPending } from "./computer";
import { CODING_PLANS, isPlanId, loginPerAgent, PLAN_IDS, type PlanId } from "./plans";

/**
 * No AI connected (ADR 0061): an agent's card when it can't answer, and the Connect step a person who
 * met it asked for. Platform-neutral (web and phone).
 */

/** A failed run's error, a routing decision's and a receipt's reason: nobody had an AI to think with. */
export const NO_AI = "no_ai";

/** `meta.noAi` of an agent's card (`bodyCode: "no_ai"`). */
export interface NoAiCard {
  agentId: string;
  /** Who asked: the Connect dialog may open by itself for them only. */
  forUserId: string | null;
  intent: ConnectIntent;
  /** With `api_key`: the provider the message named. */
  provider?: string;
  autoOpen?: boolean;
  /** Its plan's sign-in ended (`reason: not_signed_in`): the agent says so, and signing in again is the way back. */
  signedOut?: boolean;
  /**
   * Instead of `signedOut`: the computer's own sign-in of this CLI isn't there and the agent has no account, so the
   * way is an account of that CLI's plan (`planOfKind`), connected on the agent's page or from the card.
   */
  own?: MachineAccountKind;
}

const INTENTS: ReadonlySet<string> = new Set<ConnectIntent>(["claude", "chatgpt", "api_key", "any", "task"]);
const isIntent = (v: unknown): v is ConnectIntent => typeof v === "string" && INTENTS.has(v);

export function noAiCardOf(meta: Record<string, unknown> | null | undefined): NoAiCard | null {
  const raw = meta?.noAi;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.agentId !== "string") return null;
  const signedOut = o.reason === "not_signed_in";
  const own =
    signedOut && (o.own === "claude_code" || o.own === "codex" || o.own === "cursor" || o.own === "opencode")
      ? o.own
      : null;
  return {
    agentId: o.agentId,
    forUserId: typeof o.forUserId === "string" ? o.forUserId : null,
    intent: isIntent(o.intent) ? o.intent : "task",
    ...(typeof o.provider === "string" ? { provider: o.provider } : {}),
    ...(o.autoOpen === true ? { autoOpen: true } : {}),
    ...(own ? { own } : signedOut ? { signedOut: true } : {}),
  };
}

/**
 * The dialog opens by itself only where the server marked it (Jev at 0.9 or more) and at a step: never
 * for `any` or `task`, where a guess opened dialogs nobody asked for.
 */
export const opensByItself = (x: { intent?: string; autoOpen?: boolean }) =>
  x.autoOpen === true && (x.intent === "claude" || x.intent === "chatgpt" || x.intent === "api_key");

/**
 * The brain a plan thinks with, on the agent's computer: a Claude plan through Claude Code, ChatGPT's through Codex,
 * OpenCode's (`zen`: its free tier or an OpenCode key, ADR 0132) through OpenCode's CLI.
 */
export const PLAN_BRAIN: Record<"claude" | "chatgpt" | "cursor" | "zen", MachineAccountKind> = {
  claude: "claude_code",
  chatgpt: "codex",
  cursor: "cursor",
  zen: "opencode",
};
/** The plan a CLI signs in to by itself (`PLAN_BRAIN` the other way): ChatGPT's for Codex, Claude's for Claude Code. */
export const planOfKind = (kind: MachineAccountKind): "claude" | "chatgpt" | "cursor" | "zen" =>
  kind === "codex" ? "chatgpt" : kind === "cursor" ? "cursor" : kind === "opencode" ? "zen" : "claude";

type AiState = AiStatus["agents"][number]["state"];

/** An agent's state in the AI status; undefined while it loads or for an agent it doesn't list. */
export const aiStateOf = (status: AiStatus | undefined, agentId: string): AiState | undefined =>
  status?.agents.find((a) => a.agentId === agentId)?.state;

/**
 * A plan a person signs in with, on the agent's computer: Claude's, ChatGPT's, Cursor's (a sign-in or its API key,
 * ADR 0114), OpenCode's (`zen`, its free tier or an OpenCode key, ADR 0132), or a coding plan's key.
 */
export type PlanChoice = "claude" | "chatgpt" | "cursor" | "zen" | PlanId;
/** What the picker offers: a plan, or the workspace's API key. */
export type AiChoice = PlanChoice | "key";
/** Every picker's order: Claude (chosen first), ChatGPT, Cursor, OpenCode, then the coding plans. */
export const PLAN_CHOICES: readonly PlanChoice[] = ["claude", "chatgpt", "cursor", "zen", ...PLAN_IDS];
/** The account a plan signs in as: ChatGPT's through Codex, Cursor's and OpenCode's through their CLIs; the rest through Claude Code. */
export const kindOf = (choice: PlanChoice): MachineAccountKind =>
  choice === "chatgpt" || choice === "cursor" || choice === "zen" ? PLAN_BRAIN[choice] : PLAN_BRAIN.claude;
/** The plan an account signs in with: a coding plan's key, ChatGPT's through Codex, Cursor's, else Claude's. */
export const planOfAccount = (a: Pick<MachineAccount, "kind" | "provider">): PlanChoice =>
  isPlanId(a.provider) ? a.provider : planOfKind(a.kind);
/**
 * What an agent's brain view thinks with, as the picker's choice: the API key on the model brain, else the plan of the
 * account it thinks with (the computer's own sign-in: Claude's or ChatGPT's).
 */
export const aiChoiceOf = (view: Pick<AgentBrain, "brain" | "account" | "accounts">): AiChoice =>
  view.brain === "model"
    ? "key"
    : planOfAccount({ kind: view.brain, provider: view.accounts.find((a) => a.id === view.account)?.provider ?? null });
/**
 * The sign-in a row of what the person already has asks for (`existingAi`), in its plan's panel: one more login of
 * that account for the agent (`email`, Claude's hint), or a free login of it signed back in (`again`). `from`: the
 * row's login.
 */
export interface ExistingSignIn {
  choice: PlanChoice;
  from: string;
  again?: string;
  email?: string | null;
}
/** A plan's maker mark (a `ProviderLogo` id): ChatGPT's is OpenAI's, OpenCode's CLI OpenCode's. */
export const planMark = (choice: PlanChoice) =>
  choice === "chatgpt" ? "openai" : choice === "zen" ? "opencode" : choice;
/** A plan's short name, as its tile and its accounts say it: "Claude", "ChatGPT", "GLM Coding"… */
export const planLabel = (t: TFunction, choice: PlanChoice) =>
  isPlanId(choice) ? CODING_PLANS[choice].label : t(`aiAccounts.connect.${kindOf(choice)}`);
/** A plan's name in its panel, as its maker sells it: "Your Claude plan", "GLM Coding Plan"… */
export const planTitle = (t: TFunction, choice: PlanChoice) =>
  isPlanId(choice) ? CODING_PLANS[choice].name : t(`connectAi.plan.${choice}.title`);
/** The marks overlapped on the computer's plan line, and on the API key row. */
export const PLAN_MARKS = ["claude", "openai", "zai", "kimi"] as const;
export const KEY_MARKS = ["claude", "openai", "gemini", "openrouter"] as const;

/** What a plan's panel can do now for one agent: sign in, or why not yet. */
export type PlanGate =
  | { state: "loading" }
  /** The Cloud's computer on its way; `view` null: it said ready and the agent gets it on the next refresh. */
  | { state: "preparing"; view: SetupView | null }
  | { state: "offline" }
  /** Self-host with no computer for the agent (none connected). */
  | { state: "missing" }
  | { state: "unsupported"; reason: BrainUnavailable | "keys" }
  /** `owner`: the viewer doesn't own the agent; `computer`: only whoever connected it adds a sign-in there. */
  | { state: "locked"; who: "owner" | "computer" }
  | { state: "ready" };

/**
 * The gate, first match wins. `brain` is the chosen agent's view (undefined while it loads); `computer` is that
 * computer's `/ai-accounts` row, which says whether it takes a coding plan's key.
 */
export function planGate({
  choice,
  edition,
  setup,
  brain,
  computer,
}: {
  choice: PlanChoice;
  edition: Edition | null;
  setup: SetupView | null;
  brain: AgentBrain | undefined;
  computer: AiComputer | undefined;
}): PlanGate {
  if (edition === "cloud" && setupPending(setup)) return { state: "preparing", view: setup };
  if (!brain || !edition) return { state: "loading" };
  if (!brain.canUsePlans) return { state: "locked", who: "owner" };
  if (!brain.computer) return edition === "cloud" ? { state: "preparing", view: null } : { state: "missing" };
  if (!brain.computer.online) return { state: "offline" };
  const option = brain.options.find((o) => o.kind === kindOf(choice));
  if (!option?.available) return { state: "unsupported", reason: option?.reason ?? "not_installed" };
  if (!brain.accountsSupported) return { state: "unsupported", reason: "runner_old" };
  if (isPlanId(choice) && !computer?.keys) return { state: "unsupported", reason: "keys" };
  if (!brain.canManageAccounts) return { state: "locked", who: "computer" };
  return { state: "ready" };
}

/**
 * `planGate` for a person who owns no agent yet: a plan signs in on a computer they may add accounts to, and the
 * account then thinks for the agents they create there. Read off the computers' `/ai-accounts` rows (no agent, no
 * brain view) and `machines` (who connected each, its switches); `computer`: where it signs in, or the computer the
 * gate is about.
 */
export function planGateWithoutAgent({
  choice,
  edition,
  setup,
  computers,
  machines,
}: {
  choice: PlanChoice;
  edition: Edition | null;
  setup: SetupView | null;
  computers: readonly AiComputer[] | undefined;
  machines?: readonly Pick<Machine, "id" | "pairedBy" | "execAllowed" | "access">[] | undefined;
}): { gate: PlanGate; computer?: AiComputer } {
  if (edition === "cloud" && setupPending(setup)) return { gate: { state: "preparing", view: setup } };
  if (!computers || !edition) return { gate: { state: "loading" } };
  const kind = kindOf(choice);
  const machineOf = (c: AiComputer) => machines?.find((m) => m.id === c.machineId);
  const fits = (c: AiComputer) => c.online && c.brains[kind] === true && (!isPlanId(choice) || c.keys);
  const addable = computers.filter((c) => c.canAdd);
  const computer = addable.find(fits) ?? addable.find((c) => c.online) ?? addable[0];
  if (!computer) {
    // `canAdd` is also false on a runner too old for accounts: one they connected, or nobody did, is that.
    const old = computers.find((c) => c.mine || machineOf(c)?.pairedBy === null);
    if (old) return { gate: { state: "unsupported", reason: "runner_old" }, computer: old };
    const first = computers[0];
    if (!first) return { gate: edition === "cloud" ? { state: "preparing", view: null } : { state: "missing" } };
    return { gate: { state: "locked", who: "computer" }, computer: first };
  }
  if (!computer.online) return { gate: { state: "offline" }, computer };
  if (computer.brains[kind] !== true)
    return { gate: { state: "unsupported", reason: brainOffReason(machineOf(computer), kind) }, computer };
  if (isPlanId(choice) && !computer.keys) return { gate: { state: "unsupported", reason: "keys" }, computer };
  return { gate: { state: "ready" }, computer };
}

/** Why a CLI can't think on a computer, as far as its row says (the server's `brainOptions`): else not installed. */
function brainOffReason(
  m: Pick<Machine, "execAllowed" | "access"> | undefined,
  kind: MachineAccountKind,
): BrainUnavailable {
  if (m && !m.execAllowed) return "exec_off";
  const tools = m?.access?.tools;
  const on = !tools
    ? undefined
    : kind === "cursor"
      ? (tools.cursor ?? tools.codex)
      : tools[kind === "codex" ? "codex" : "claude"];
  return on === false ? "tool_off" : "not_installed";
}

/**
 * The person's account of this plan that can think for one more agent (signed in, and free or made for all of their
 * agents), as "Quem pensa" offers it: what a plan connected with no agent yet left them.
 */
export const freePlanAccount = <
  A extends Pick<MachineAccount, "kind" | "provider" | "mine" | "status" | "shared" | "usedBy">,
>(
  accounts: readonly A[] | undefined,
  choice: PlanChoice,
) =>
  accounts?.find(
    (a) =>
      a.mine && a.status === "active" && (a.shared === true || a.usedBy.length === 0) && planOfAccount(a) === choice,
  );

/**
 * The computer's own sign-in can think for the agent with this plan (`ownSignInReady`): Claude's or ChatGPT's, never
 * a coding plan's, which signs in with its key.
 */
export const ownSignInFor = (choice: PlanChoice, brain: AgentBrain | undefined, computer: AiComputer | undefined) =>
  !isPlanId(choice) && ownSignInReady(brain, computer, kindOf(choice));

/** A plan tile reads dimmed when its plan can't sign in there at all (it still opens its panel, which says why). */
export const gateDims = (gate: PlanGate) => gate.state === "missing" || gate.state === "unsupported";

/** The option a message asked for (Jev, a card); null for `any` and `task`. */
export const choiceOfIntent = (intent: ConnectIntent | undefined): AiChoice | null =>
  intent === "claude" || intent === "chatgpt" ? intent : intent === "api_key" ? "key" : null;

/**
 * The option chosen when the picker opens: what was asked for; the API key for someone who owns no agent but may
 * add keys, or where Claude can't sign in at all (self-host with no computer); else the plan the agent it's for
 * thinks with (`plan`, `agentPlan`), else Claude.
 */
export function defaultChoice({
  intent,
  ownsAgents,
  mayAddKey,
  claudeGate,
  plan,
}: {
  intent?: ConnectIntent;
  ownsAgents: boolean;
  mayAddKey: boolean;
  claudeGate: PlanGate;
  plan?: PlanChoice | null;
}): AiChoice {
  const asked = choiceOfIntent(intent);
  if (asked) return asked;
  if (!ownsAgents && mayAddKey) return "key";
  return claudeGate.state === "missing" ? "key" : (plan ?? "claude");
}

/** "For which agent": the one asked for, else the first with no AI, else the first. */
export const firstAgent = <A extends { id: string }>(
  agents: readonly A[],
  hasAi: (id: string) => boolean,
  asked?: string,
) => agents.find((a) => a.id === asked) ?? agents.find((a) => !hasAi(a.id)) ?? agents[0];

/** A plan signs in once per agent with the same account (`LOGIN_PER_AGENT`), so it can think for all of them. */
export const signsInPerAgent = (choice: PlanChoice) =>
  // OpenCode's account is its key alone (`zen`), like a coding plan's.
  loginPerAgent(kindOf(choice), isPlanId(choice) || choice === "zen" ? choice : null);

/** A plan's row in a computer's `shares` (`AiComputer`): Claude's and ChatGPT's sign-ins, or a coding plan's key. */
export const shareOf = (choice: PlanChoice): keyof NonNullable<AiComputer["shares"]> =>
  isPlanId(choice) ? "key" : kindOf(choice);

/** One sign-in of this plan on that computer thinks for all of the viewer's agents there (protocol 29). */
export const signsInOnce = (choice: PlanChoice, computer: Pick<AiComputer, "shares"> | undefined) =>
  computer?.shares?.[shareOf(choice)] === true;

/** What an agent thinks with now: a plan, the workspace's API key (`key`), or nothing yet (null). */
export type AgentAi = AiChoice | null;

/** What an agent thinks with (`AgentAi`), from its row's brain and its state in the AI status. */
export function agentAi(agent: Pick<Agent, "brain"> | undefined, state: AiState | undefined): AgentAi {
  const brain = agent?.brain;
  if (brain?.kind === "codex") return "chatgpt";
  if (brain?.kind === "cursor") return "cursor";
  if (brain?.kind === "opencode") return "zen";
  if (brain?.kind === "claude_code") return isPlanId(brain.provider) ? brain.provider : "claude";
  return state === undefined || state === "no_ai" ? null : "key";
}

/** The plan an agent's computer brain signs in with, where its Connect opens; null on the model brain. */
export function agentPlan(agent: Pick<Agent, "brain"> | undefined): PlanChoice | null {
  const ai = agentAi(agent, undefined);
  return ai === "key" ? null : ai;
}

/**
 * The plan a card's Sign in again opens on: the agent's own, where the card says its sign-in ended and the agent
 * still thinks with a plan; null otherwise (Connect an AI, at the card's step).
 */
export const signInAgainPlan = (card: Pick<NoAiCard, "signedOut">, agent: Pick<Agent, "brain"> | undefined) =>
  card.signedOut ? agentPlan(agent) : null;

/** The "For" select's choice that signs a plan in for every agent of `allAgents`. */
export const ALL_AGENTS = "all";

/**
 * "All agents" for a plan: the viewer's agents that have no AI yet, and those already on this plan (they read as
 * done), on the computer of the first with no AI (else of the first on this plan). An agent on the API key stays
 * out, since a plan makes an agent answer only its owner and one on the key may be answering the team. One on
 * another plan keeps it. Empty where the plan's account thinks for one agent there (neither one sign-in for all,
 * `sharedOn`, nor one login per agent), and below two agents, where the choice is that one agent.
 */
export function allAgents<A extends { id: string }>({
  choice,
  agents,
  aiOf,
  machineOf,
  sharedOn,
}: {
  choice: PlanChoice;
  /** The viewer's agents, in team order. */
  agents: readonly A[];
  aiOf: (agentId: string) => AgentAi;
  /** The computer an agent works on; null or undefined while it has none. */
  machineOf: (agentId: string) => string | null | undefined;
  /** One sign-in of this plan thinks for all of the viewer's agents on that computer (`signsInOnce`). */
  sharedOn?: (machineId: string | null) => boolean;
}): A[] {
  const open = agents.filter((a) => {
    const ai = aiOf(a.id);
    return ai === null || ai === choice;
  });
  const first = open.find((a) => aiOf(a.id) === null) ?? open[0];
  const machine = first ? (machineOf(first.id) ?? null) : null;
  if (!signsInPerAgent(choice) && !sharedOn?.(machine)) return [];
  const set = open.filter((a) => (machineOf(a.id) ?? null) === machine);
  return set.length >= 2 ? set : [];
}

/**
 * Whom a plan's panel is for: the agent picked or asked for, all of `set` (`allAgents`) when picked or while two or
 * more of them have no AI yet, else the first agent with no AI (`host` before the others), else the first. "All
 * agents" picked where the plan signs in once per account is that same agent. With no set it is the agent whose
 * computer the panel reads, the same for "All agents", which is on that agent's computer.
 */
export function defaultTarget<A extends { id: string }>({
  agents,
  set,
  hasAi,
  asked,
  picked,
  host,
}: {
  agents: readonly A[];
  set: readonly { id: string }[];
  hasAi: (agentId: string) => boolean;
  asked?: string;
  /** What the "For" select says (`ALL_AGENTS` or an agent's id), once someone chose. */
  picked?: string;
  /** The agent whose face asks the question (the setup's AI step): the one the panel is for, while it has no AI. */
  host?: string;
}): string | undefined {
  const first = (agents.find((a) => a.id === host && !hasAi(a.id)) ?? firstAgent(agents, hasAi))?.id;
  if (picked === ALL_AGENTS) return set.length >= 2 ? ALL_AGENTS : first;
  for (const id of [picked, asked]) if (id && agents.some((a) => a.id === id)) return id;
  if (set.filter((a) => !hasAi(a.id)).length >= 2) return ALL_AGENTS;
  return first;
}

/**
 * Every agent with an AI is the viewer's and answers only them, so someone invited now has no agent to talk to (the
 * note before inviting). False while no agent has an AI.
 */
export function onlyOwnerReplies(agents: readonly Agent[], hasAi: (agentId: string) => boolean, viewerId: string) {
  const thinking = agents.filter((a) => hasAi(a.id));
  return (
    thinking.length > 0 &&
    thinking.every((a) => a.ownerUserId === viewerId && !answersOthers(audienceOf(a), a.ownerUserId))
  );
}
